import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "@/api/adminOneClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — Signal Command™ (Phase 26G).
 *
 * Unified NiSi-scored signal table across all domains with:
 *   • Domain filters (Markets, Crypto, FX, Sports, Healthcare, Startups, Africa)
 *   • Severity filter
 *   • HITL queue (review / approve / escalate)
 *   • Watchlist export (JSON download)
 * Read-only by design; no autonomous execution.
 */

const DOMAINS = [
  { id: "markets",    label: "Markets",    accent: "#10b981", method: "intelligenceTopOpps", asset: "stocks" },
  { id: "crypto",     label: "Crypto",     accent: "#fcd34d", method: "intelligenceCrypto", asset: "crypto" },
  { id: "fx",         label: "FX",         accent: "#67e8f9", method: "intelligenceForex", asset: "forex" },
  { id: "sports",     label: "Sports",     accent: "#a78bfa", method: "intelligenceSports", asset: "sports" },
  { id: "healthcare", label: "Healthcare", accent: "#fda4af", method: "intelligenceHealthcare", asset: "healthcare" },
  { id: "startups",   label: "Startups",   accent: "#c5a059", method: "startupsPublic", asset: "startups" },
  { id: "africa",     label: "Africa",     accent: "#f59e0b", method: "intelligenceAfrica", asset: "african" },
];

const SEVERITIES = [
  { id: "all",      label: "All",      color: "#94a3b8" },
  { id: "extreme",  label: "Extreme",  color: "#ef4444", min: 80 },
  { id: "high",     label: "High",     color: "#fb923c", min: 65, max: 80 },
  { id: "medium",   label: "Medium",   color: "#fcd34d", min: 50, max: 65 },
  { id: "low",      label: "Low",      color: "#94a3b8", min: 0,  max: 50 },
];

const LS_HITL = "carelogix_signalcmd_hitl_v1";

function loadHitl() {
  try {
    const raw = localStorage.getItem(LS_HITL);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveHitl(items) {
  try { localStorage.setItem(LS_HITL, JSON.stringify(items)); } catch { /* ignore */ }
}

function severityFor(score) {
  const v = Number(score || 0);
  if (v >= 80) return "extreme";
  if (v >= 65) return "high";
  if (v >= 50) return "medium";
  return "low";
}

function exportJson(rows, label = "signal_command_watchlist") {
  try {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${label}_${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error("export failed", e);
  }
}

export default function OracleSignalCommand() {
  const [selDomains, setSelDomains] = useState(DOMAINS.map((d) => d.id));
  const [selSeverity, setSelSeverity] = useState("all");
  const [rows, setRows] = useState([]);
  const [hitl, setHitl] = useState(loadHitl);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");

  const toggleDomain = (id) => {
    setSelDomains((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const all = [];
      await Promise.all(
        DOMAINS.map(async (d) => {
          try {
            const fn = adminApi[d.method];
            const { data: r } = await fn();
            let items = [];
            if (Array.isArray(r)) items = r;
            else if (Array.isArray(r?.items)) items = r.items;
            else if (Array.isArray(r?.opportunities)) items = r.opportunities;
            else if (Array.isArray(r?.signals)) items = r.signals;
            else if (Array.isArray(r?.cryptos)) items = r.cryptos;
            else if (Array.isArray(r?.pairs)) items = r.pairs;
            else if (Array.isArray(r?.events)) items = r.events;
            else if (Array.isArray(r?.markets)) items = r.markets;
            else if (Array.isArray(r?.matches)) items = r.matches;
            else if (Array.isArray(r?.sectors)) items = r.sectors;
            items.forEach((it) => {
              all.push({
                domain: d.id,
                domain_label: d.label,
                accent: d.accent,
                symbol: it.symbol || it.name || it.pair || "—",
                asset_class: it.asset_class || d.asset,
                direction: (it.direction || "neutral").toLowerCase(),
                opportunity_score: Number(it.opportunity_score || 0),
                confidence_score: Number(it.confidence_score || 0),
                risk_score: Number(it.risk_score || 0),
                pct_change_24h: typeof it.pct_change_24h === "number" ? it.pct_change_24h : null,
                horizon: it.horizon || null,
                region: it.region || null,
                sector: it.sector || null,
                expected_range: it.expected_range || null,
              });
            });
          } catch (e) {
            // single-domain failure is non-blocking
          }
        })
      );
      setRows(all);
    } catch (e) {
      setErr(e?.message || "load_failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const sev = SEVERITIES.find((x) => x.id === selSeverity);
    let r = rows.filter((row) => selDomains.includes(row.domain));
    if (search) {
      const q = search.toLowerCase();
      r = r.filter((row) => `${row.symbol} ${row.sector || ""} ${row.region || ""}`.toLowerCase().includes(q));
    }
    if (sev && sev.id !== "all") {
      const min = sev.min ?? 0;
      const max = sev.max ?? 101;
      r = r.filter((row) => row.opportunity_score >= min && row.opportunity_score < max);
    }
    r = [...r].sort((a, b) => (b.opportunity_score - a.opportunity_score));
    return r;
  }, [rows, selDomains, selSeverity, search]);

  const addToHitl = useCallback((row, action = "review") => {
    const next = [
      {
        id: `hitl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`,
        action,
        ts: new Date().toISOString(),
        ...row,
        status: "queued",
      },
      ...hitl,
    ].slice(0, 50);
    setHitl(next);
    saveHitl(next);
  }, [hitl]);

  const updateHitl = useCallback((id, patch) => {
    const next = hitl.map((row) => (row.id === id ? { ...row, ...patch } : row));
    setHitl(next);
    saveHitl(next);
  }, [hitl]);

  const clearHitl = useCallback(() => {
    setHitl([]); saveHitl([]);
  }, []);

  return (
    <div style={C.canvas} data-testid="signal-command-page">
      <ComplianceLock variant="banner" testid="signal-command-compliance-top" />
      <header style={C.header}>
        <div>
          <h1 style={C.title}>Signal Command™ · ORACLE Signal Fabric™</h1>
          <p style={C.subtitle}>
            Unified NiSi-scored signals across 7 domains with HITL queue and watchlist export.
            Research / education only. Human review required before any action.
          </p>
        </div>
        <div style={C.headerActions}>
          <span style={C.livePill}>LIVE · 7 DOMAINS</span>
          <button type="button" onClick={load} disabled={busy} style={C.refresh} data-testid="signal-command-refresh">
            {busy ? "…" : "↻ Refresh"}
          </button>
        </div>
      </header>

      {err && <div style={C.err}>⚠ {err}</div>}

      {/* Filters */}
      <section style={C.filterBar}>
        <div style={C.chipRow}>
          {DOMAINS.map((d) => {
            const on = selDomains.includes(d.id);
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => toggleDomain(d.id)}
                style={{
                  ...C.chip,
                  color: on ? d.accent : "#475569",
                  borderColor: on ? d.accent : "rgba(255,255,255,0.10)",
                  background: on ? `${d.accent}1a` : "transparent",
                }}
                data-testid={`signal-domain-${d.id}`}
                aria-pressed={on}
              >
                {d.label}
              </button>
            );
          })}
        </div>
        <div style={C.chipRow}>
          {SEVERITIES.map((s) => {
            const on = selSeverity === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelSeverity(s.id)}
                style={{
                  ...C.chip,
                  color: on ? s.color : "#475569",
                  borderColor: on ? s.color : "rgba(255,255,255,0.10)",
                  background: on ? `${s.color}1a` : "transparent",
                }}
                data-testid={`signal-severity-${s.id}`}
                aria-pressed={on}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <input
          type="search"
          placeholder="Search symbol / sector / region…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={C.search}
          data-testid="signal-command-search"
        />
        <button
          type="button"
          onClick={() => exportJson(filtered, "signal_command_filtered")}
          style={C.exportBtn}
          data-testid="signal-export-filtered"
        >⬇ Export Filtered</button>
      </section>

      {/* Signal Table */}
      <section style={C.tableWrap} data-testid="signal-command-table">
        <div style={C.tableHead}>
          <span style={C.colSym}>SYMBOL</span>
          <span style={C.colDom}>DOMAIN</span>
          <span style={C.colDir}>DIR</span>
          <span style={C.colScore}>OPP</span>
          <span style={C.colScore}>CONF</span>
          <span style={C.colScore}>RISK</span>
          <span style={C.colSev}>SEV</span>
          <span style={C.colActions}>HITL</span>
        </div>
        {filtered.length === 0 ? (
          <p style={C.empty}>{busy ? "Loading…" : "No signals match the current filters."}</p>
        ) : (
          <div style={C.tableBody}>
            {filtered.map((row, i) => {
              const sev = severityFor(row.opportunity_score);
              const sevMeta = SEVERITIES.find((s) => s.id === sev) || {};
              const dirColor = row.direction === "bullish" ? "#10b981" : row.direction === "bearish" ? "#ef4444" : "#c5a059";
              return (
                <div key={`${row.domain}-${row.symbol}-${i}`} style={C.row} data-testid={`signal-row-${i}`}>
                  <span style={{ ...C.colSym, ...C.sym }}>{row.symbol}</span>
                  <span style={{ ...C.colDom, color: row.accent }}>{row.domain_label}</span>
                  <span style={{ ...C.colDir, color: dirColor, fontWeight: 800 }}>{row.direction.toUpperCase()}</span>
                  <span style={C.colScore}>{row.opportunity_score}</span>
                  <span style={{ ...C.colScore, color: "#10b981" }}>{row.confidence_score}</span>
                  <span style={{ ...C.colScore, color: "#fca5a5" }}>{row.risk_score}</span>
                  <span style={{ ...C.colSev, color: sevMeta.color || "#94a3b8" }}>{(sev || "").toUpperCase()}</span>
                  <span style={C.colActions}>
                    <button type="button" onClick={() => addToHitl(row, "review")} style={C.tinyReview} data-testid={`signal-review-${i}`}>Review</button>
                    <button type="button" onClick={() => addToHitl(row, "approve")} style={C.tinyApprove} data-testid={`signal-approve-${i}`}>Approve</button>
                    <button type="button" onClick={() => addToHitl(row, "escalate")} style={C.tinyEscalate} data-testid={`signal-escalate-${i}`}>Escalate</button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* HITL queue */}
      <section style={C.panel} data-testid="signal-command-hitl-queue">
        <div style={C.panelHead}>
          <h2 style={C.heading}>HITL Queue · Human-In-The-Loop</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={() => exportJson(hitl, "signal_command_hitl_queue")} style={C.exportBtn} data-testid="signal-export-hitl">
              ⬇ Export Queue
            </button>
            <button type="button" onClick={clearHitl} style={C.ghost} data-testid="signal-clear-hitl">
              Clear
            </button>
          </div>
        </div>
        {hitl.length === 0 ? (
          <p style={C.empty}>No signals queued. Use Review / Approve / Escalate on any row above.</p>
        ) : (
          <div style={C.hitlGrid}>
            {hitl.map((row) => (
              <div key={row.id} style={C.hitlCard} data-testid={`hitl-card-${row.id}`}>
                <div style={C.hitlHead}>
                  <strong style={{ color: row.accent }}>{row.symbol}</strong>
                  <span style={{ ...C.pill, color: row.action === "approve" ? "#10b981" : row.action === "escalate" ? "#ef4444" : "#c5a059", borderColor: "currentColor" }}>
                    {row.action.toUpperCase()}
                  </span>
                </div>
                <div style={C.metaRow}>
                  <span style={C.metaLbl}>DOMAIN</span>
                  <code style={C.metaVal}>{row.domain_label}</code>
                </div>
                <div style={C.metaRow}>
                  <span style={C.metaLbl}>OPP / CONF / RISK</span>
                  <code style={C.metaVal}>{row.opportunity_score} / {row.confidence_score} / {row.risk_score}</code>
                </div>
                <div style={C.metaRow}>
                  <span style={C.metaLbl}>STATUS</span>
                  <code style={{ ...C.metaVal, color: row.status === "approved" ? "#10b981" : row.status === "rejected" ? "#ef4444" : "#fcd34d" }}>
                    {row.status?.toUpperCase()}
                  </code>
                </div>
                <div style={C.hitlActions}>
                  <button type="button" onClick={() => updateHitl(row.id, { status: "approved" })} style={C.tinyApprove} data-testid={`hitl-approve-${row.id}`}>✓ Approve</button>
                  <button type="button" onClick={() => updateHitl(row.id, { status: "rejected" })} style={C.tinyEscalate} data-testid={`hitl-reject-${row.id}`}>✕ Reject</button>
                </div>
                <ComplianceLock variant="inline" short testid={`hitl-${row.id}-compliance`} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const C = {
  canvas: { minHeight: "100vh", background: "radial-gradient(circle at top left, rgba(16,185,129,0.10), #040608 30%, #020304 100%)", color: "#f8fafc", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  header: { padding: "20px 26px 16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  title: { color: "#c5a059", fontSize: 24, margin: 0, fontWeight: 800 },
  subtitle: { color: "#94a3b8", fontSize: 13, maxWidth: 820, marginTop: 6 },
  headerActions: { display: "flex", gap: 8, alignItems: "center" },
  livePill: { color: "#10b981", border: "1px solid rgba(16,185,129,0.35)", background: "rgba(16,185,129,0.10)", padding: "6px 11px", borderRadius: 999, fontSize: 10.5, fontWeight: 900, fontFamily: "monospace", letterSpacing: "0.10em" },
  refresh: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.10em" },
  err: { margin: "10px 26px 0", padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12.5 },
  filterBar: { display: "flex", gap: 12, alignItems: "center", padding: "16px 26px 0", flexWrap: "wrap" },
  chipRow: { display: "flex", gap: 6, flexWrap: "wrap" },
  chip: { appearance: "none", padding: "5px 12px", borderRadius: 999, border: "1px solid", cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.06em", fontWeight: 800 },
  search: { appearance: "none", background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: "#e2e8f0", padding: "8px 12px", borderRadius: 10, fontSize: 12.5, width: 240, fontFamily: "'Figtree', system-ui, sans-serif" },
  exportBtn: { appearance: "none", background: "transparent", border: "1px solid rgba(197,160,89,0.45)", color: "#c5a059", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.06em", fontWeight: 800 },
  ghost: { appearance: "none", background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#94a3b8", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  tableWrap: { margin: "16px 26px", background: "rgba(11,15,20,0.94)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden" },
  tableHead: { display: "grid", gridTemplateColumns: "minmax(120px, 1.4fr) 1fr 80px 70px 70px 70px 80px 1.4fr", gap: 8, padding: "10px 14px", background: "rgba(0,0,0,0.30)", borderBottom: "1px solid rgba(255,255,255,0.06)", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "#64748b" },
  tableBody: { maxHeight: 480, overflowY: "auto" },
  row: { display: "grid", gridTemplateColumns: "minmax(120px, 1.4fr) 1fr 80px 70px 70px 70px 80px 1.4fr", gap: 8, padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 12, alignItems: "center" },
  colSym: { color: "#e2e8f0" },
  sym: { fontFamily: "'Space Mono', monospace" },
  colDom: { fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  colDir: { fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.06em" },
  colScore: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 12.5, fontWeight: 800 },
  colSev: { fontFamily: "'Space Mono', monospace", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em" },
  colActions: { display: "flex", gap: 5, flexWrap: "wrap" },
  tinyReview: { appearance: "none", background: "rgba(197,160,89,0.10)", border: "1px solid rgba(197,160,89,0.45)", color: "#c5a059", padding: "3px 8px", borderRadius: 6, cursor: "pointer", fontSize: 10, fontFamily: "'Space Mono', monospace", fontWeight: 800 },
  tinyApprove: { appearance: "none", background: "rgba(16,185,129,0.10)", border: "1px solid #10b981", color: "#10b981", padding: "3px 8px", borderRadius: 6, cursor: "pointer", fontSize: 10, fontFamily: "'Space Mono', monospace", fontWeight: 800 },
  tinyEscalate: { appearance: "none", background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", color: "#ef4444", padding: "3px 8px", borderRadius: 6, cursor: "pointer", fontSize: 10, fontFamily: "'Space Mono', monospace", fontWeight: 800 },
  panel: { margin: "16px 26px 30px", background: "rgba(11,15,20,0.94)", border: "1px solid rgba(197,160,89,0.14)", borderRadius: 16, padding: 18 },
  panelHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 8 },
  heading: { color: "#e2e8f0", margin: 0, fontSize: 16 },
  hitlGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 },
  hitlCard: { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 5 },
  hitlHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  pill: { fontSize: 10, fontFamily: "'Space Mono', monospace", letterSpacing: "0.10em", fontWeight: 800, padding: "3px 8px", border: "1px solid", borderRadius: 999 },
  metaRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  metaLbl: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.10em" },
  metaVal: { background: "rgba(255,255,255,0.04)", color: "#cbd5e1", padding: "2px 6px", borderRadius: 6, fontFamily: "'Space Mono', monospace", fontSize: 11 },
  hitlActions: { display: "flex", gap: 6, marginTop: 6 },
  empty: { color: "#94a3b8", textAlign: "center", padding: 28, fontFamily: "'Space Mono', monospace", fontSize: 12.5, margin: 0 },
};
